import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Util } from 'core/services';
import { StateService, UIRouterGlobals } from '@uirouter/core';
import { XmcPlanSwitchService } from 'my-account/activity/shared/services/xmc-plan-switch.service';
import { XmcPayloadConstants } from 'my-account/constants';

interface DeviceSelectionEvent {
    device: Device;
    isSelected: boolean;
    index: number;
}

interface Device {
    id?: string;
    nickname: string;
    number: string;
    xmcPrice: string;
    footerText: string;
    imgUrl?: string;
    lineId?: string;
    lineIdKey?: string;
    deviceIdKey?: string;
    planId?: string;
    price?: string;
}

type XmcPlanSwitchPayload = {
    requestType: string;
    items: {
        lineId: string;
        newPlanId?: string;
        changeType: string;
        action: string;
        sharedBucketId?: string;
        additionalOffers?: {
            changeType: string;
            offerAction: string;
        }[];
    }[];
};

type XmcPlan8SwitchPayload = {
    planId: string;
    lineIdKey: string;
    insuranceItems: {
        lineId: string;
        plan: {
            planId: string;
            action: string;
            planGroup: string;
        };
    }[];
};

@Component({
    selector: 'xmc-consent-page',
    styleUrls: ['./xmc-consent-page.scss'],
    templateUrl: './xmc-consent-page.html',
    standalone: false
})
export class XmcConsentPageComponent implements OnInit, OnDestroy {
    public lineId: string;
    public planCode: string;
    public xmcConsentPageData: XMCSelectionPageDetails;
    public selectedLineIdKeys: string[] = [];
    public selectedLineIds: string[] = [];
    public consentAgreed: boolean = false;
    public supportLinkContent: string = '';
    public primaryDevice: Device;

    private subscriptions: Subscription[] = [];
    private state: StateService;
    private globalRouterState: UIRouterGlobals;

    private xmcPlanSwitchService: XmcPlanSwitchService;

    constructor(state: StateService, globalRouterState: UIRouterGlobals, xmcPlanSwitchService: XmcPlanSwitchService) {
        Object.assign(this, { state, globalRouterState, xmcPlanSwitchService });
    }

    public ngOnInit(): void {
        this.lineId = this.globalRouterState.params.line_id;
        this.planCode = this.globalRouterState.params.plan_code;
        this.xmcConsentPageData = this.globalRouterState.params.xmcConsent;
        this.supportLinkContent = this.xmcConsentPageData?.features?.supportLinkContent || '';
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    /**
     * Handles device selection changes
     * @param event Selection event from device card component
     */
    public onDeviceSelectionChange(event: DeviceSelectionEvent): void {
        const lineIdKey: string = event.device.lineIdKey;
        const lineId: string = event.device.lineId;

        if (event.isSelected) {
            // Add lineIdKey to selected list if it exists and not already present
            if (lineIdKey && !this.selectedLineIdKeys.includes(lineIdKey)) {
                this.selectedLineIdKeys.push(lineIdKey);
            }
            // Add lineId to selected list if it exists and not already present
            if (lineId && !this.selectedLineIds.includes(lineId)) {
                this.selectedLineIds.push(lineId);
            }
        } else {
            // Remove lineIdKey from selected list
            if (lineIdKey) {
                const lineIdKeyIndex: number = this.selectedLineIdKeys.indexOf(lineIdKey);
                if (lineIdKeyIndex > -1) {
                    this.selectedLineIdKeys.splice(lineIdKeyIndex, 1);
                }
            }
            // Remove lineId from selected list
            if (lineId) {
                const lineIdIndex: number = this.selectedLineIds.indexOf(lineId);
                if (lineIdIndex > -1) {
                    this.selectedLineIds.splice(lineIdIndex, 1);
                }
            }
        }
    }

    /**
     * Toggles the consent agreement checkbox state
     */
    public toggleCheckboxSelection(): void {
        this.consentAgreed = !this.consentAgreed;
    }

    /**
     * Navigates back to the previous step
     */
    public onBack(): void {
        this.state.go('account.activity.details.plan-switch.xm-unl-target-plan-review', { line_id: this.lineId, plan_code: this.planCode });
    }

    /**
     * Handles continue action when user agrees to terms
     * Constructs payload, stores in service, and navigates to review order page
     * Only executes if consent has been agreed to
     */
    public onContinue(): void {
        if (!this.consentAgreed) {
            return;
        }
        const payload: XmcPlanSwitchPayload = this.constructXmcPlanSwitchPayload();
        this.xmcPlanSwitchService.setxmcConsentPayloadBillPreview(payload);
        this.xmcPlanSwitchService.setSelectedLineIdKeys(this.selectedLineIdKeys);
        this.xmcPlanSwitchService.setSelectedLineIds(this.selectedLineIds);
        const plan8Payload: XmcPlan8SwitchPayload = this.constructXmcPlan8SwitchPayload();
        this.xmcPlanSwitchService.setXmcPayloadForPlanSwitch(plan8Payload);
        this.state.go('account.activity.details.plan-switch.review-order', { line_id: this.lineId, plan_code: this.planCode, backButtonPath: 'account.activity.details.plan-switch.xm-unl-target-plan-review' });
    }

    /**
     * Constructs the payload for XMC plan switch with device re-arrangement
     * Always respects device order from allDevices, regardless of selection order
     * Primary device first (with lineId or empty), then connected devices
     * @returns XmcPlanSwitchPayload with properly ordered items
     */
    private constructXmcPlanSwitchPayload(): XmcPlanSwitchPayload {
        // Determine primary lineId: use first device if selected, otherwise empty
        this.primaryDevice = this.xmcConsentPageData?.devices?.items?.[0] || undefined;
        const primaryLineId: string = this.primaryDevice?.lineId && this.selectedLineIds.includes(this.primaryDevice.lineId)
            ? this.primaryDevice.lineId
            : '';

        // Get all selected devices(connected devices) excluding primary device
        const connectedLineIds: string[] = this.selectedLineIds
            .filter((lineId: string) => lineId !== primaryLineId);

        // Build items array
        const items: XmcPlanSwitchPayload['items'] = [
            this.buildPrimaryDeviceItem(primaryLineId),
            ...this.buildConnectedDevicesItems(connectedLineIds)
        ];

        return {
            requestType: XmcPayloadConstants.PRIMARY_CHANGE_TYPE,
            items: items
        };
    }

    /**
     * Builds the primary device item for plan change with insurance offer
     * lineId can be empty if primary device is not selected
     * @param primaryLineId Line ID of primary device (can be empty string if not selected)
     * @returns Primary device item object
     */
    private buildPrimaryDeviceItem(primaryLineId: string): XmcPlanSwitchPayload['items'][number] {
        return {
            lineId: primaryLineId,
            newPlanId: this.getPlanCodeFromParams(this.planCode),
            changeType: XmcPayloadConstants.PRIMARY_CHANGE_TYPE,
            action: XmcPayloadConstants.PRIMARY_ACTION,
            sharedBucketId: '',
            additionalOffers: [{
                changeType: XmcPayloadConstants.INSURANCE_CHANGE_TYPE,
                offerAction: XmcPayloadConstants.INSURANCE_ACTION
            }]
        };
    }

    /**
     * Builds items for connected devices (insurance adds only)
     * @param connectedLineIds Array of connected device line IDs
     * @returns Array of connected device items
     */
    private buildConnectedDevicesItems(connectedLineIds: string[]): XmcPlanSwitchPayload['items'] {
        return connectedLineIds.map((lineId: string) => ({
            lineId: lineId,
            changeType: XmcPayloadConstants.INSURANCE_CHANGE_TYPE,
            action: XmcPayloadConstants.INSURANCE_ACTION
        }));
    }

    /**
     * Constructs the payload for SWITCH_PLAN8_VIA_SSM_PROXY
     * Uses planCode and selectedLineIdKeys to build planId, lineIdKey and insuranceItems
     * @returns XmcPlan8SwitchPayload ready to be stored in service
     */
    private constructXmcPlan8SwitchPayload(): XmcPlan8SwitchPayload {
        const devices: Device[] = this.xmcConsentPageData?.devices?.items || [];

        return {
            planId: this.planCode,
            lineIdKey: this.selectedLineIdKeys[0] || '',
            insuranceItems: this.selectedLineIds.map((lineId: string) => {
                const device: Device = devices.find((d: Device) => d.lineId === lineId);

                return {
                    lineId: lineId,
                    plan: {
                        planId: device?.planId || '',
                        action: 'ADD',
                        planGroup: 'XMC',
                        price: device?.price || 0
                    }
                };
            })
        };
    }

    /**
     * Retrun only plancode from route parameters.
     */
    private getPlanCodeFromParams(planCode: string): string {
        return planCode?.includes(':') ? planCode?.split(':')[0] : planCode;
    }
}
